import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowRight, X, Bell, CheckCircle2, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AppointmentAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime, formatTime } from "@/lib/format";
import { useFormatters } from "@/lib/format";
import { toast } from "sonner";

const STATUS_PILL = {
    scheduled:  "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    confirmed:  "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
    checked_in: "bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red",
    converted:  "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    cancelled:  "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
    no_show:    "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300",
};

export default function AppointmentDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { permissions } = useAuth();
    const [appt, setAppt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    async function load() {
        setLoading(true);
        try { setAppt(await AppointmentAPI.get(id)); }
        catch (e) { toast.error(extractError(e)); nav("/appointments"); }
        finally { setLoading(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    if (loading || !appt) return <div className="grid place-items-center p-12"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

    async function doCheckIn() {
        try {
            await AppointmentAPI.checkIn(id, {});
            toast.success("Checked in");
            load();
        } catch (e) { toast.error(extractError(e)); }
    }

    async function doConvert() {
        try {
            const r = await AppointmentAPI.convertToJob(id);
            toast.success(`Job card ${r.job_number}`);
            nav(`/jobs/${r.job_card_id}`);
        } catch (e) { toast.error(extractError(e)); }
    }

    async function doRemind() {
        try { await AppointmentAPI.sendReminder(id); toast.success("Reminder queued"); load(); }
        catch (e) { toast.error(extractError(e)); }
    }

    async function doCancel() {
        if (!cancelReason || cancelReason.length < 2) { toast.error("Reason required"); return; }
        try { await AppointmentAPI.cancel(id, { reason: cancelReason }); toast.success("Appointment cancelled"); setShowCancel(false); load(); }
        catch (e) { toast.error(extractError(e)); }
    }

    const canCheckIn   = permissions.includes("appointment:update") && (appt.status === "scheduled" || appt.status === "confirmed");
    const canCancel    = permissions.includes("appointment:cancel") && ["scheduled","confirmed","checked_in"].includes(appt.status);
    const alreadyConverted = appt.status === "converted" || !!appt.converted_job_card_id;
    const canConvert   = permissions.includes("jobcard:create") && appt.status !== "cancelled" && !alreadyConverted;
    const canNotify    = permissions.includes("appointment:notify") && appt.status !== "cancelled";

    return (
        <div className="space-y-6 animate-fade-in" data-testid="appointment-detail-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <Link to="/appointments" className="text-xs text-zinc-500 hover:underline">← Appointments</Link>
                    <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                        {formatDateTime(appt.scheduled_start)}
                    </h1>
                    <div className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_PILL[appt.status] || ""}`}>
                            {appt.status.replace(/_/g, " ")}
                        </span>
                        <span>· {formatTime(appt.scheduled_start)} → {formatTime(appt.scheduled_end)}</span>
                        {appt.priority === "high" && <span className="inline-flex rounded bg-brand-red/15 px-1.5 py-0.5 text-[10px] uppercase text-brand-redDeep">priority high</span>}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {canCheckIn && <Button variant="outline" onClick={doCheckIn} data-testid="appt-checkin"><CheckCircle2 className="mr-1 h-4 w-4" /> Check in</Button>}
                    {canConvert && <Button onClick={doConvert} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="appointment-convert-to-job"><Wrench className="mr-1 h-4 w-4" /> Convert to job</Button>}
                    {alreadyConverted && (
                        <span
                            data-testid="appointment-already-converted-badge"
                            className="inline-flex items-center gap-1 rounded border border-emerald-400/40 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
                        >
                            <Wrench className="h-3 w-3" /> already converted to job
                        </span>
                    )}
                    {canNotify && <Button variant="outline" onClick={doRemind} data-testid="appt-remind"><Bell className="mr-1 h-4 w-4" /> Send reminder</Button>}
                    {canCancel && <Button variant="outline" onClick={() => setShowCancel(true)} data-testid="appt-cancel-btn"><X className="mr-1 h-4 w-4" /> Cancel</Button>}
                </div>
            </header>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card className="p-6">
                    <h2 className="mb-3 font-display text-lg font-bold tracking-tight">Customer &amp; vehicle</h2>
                    <Row label="Customer" value={
                        <Link to={`/customers/${appt.customer?.id}`} className="text-brand-redDeep hover:underline">
                            {appt.customer?.display_name}
                        </Link>
                    } />
                    <Row label="Phone" value={<span className="font-mono">{appt.customer?.phone || "—"}</span>} />
                    <Row label="Vehicle" value={
                        <Link to={`/vehicles/${appt.vehicle?.id}`} className="text-brand-redDeep hover:underline font-mono">
                            {appt.vehicle?.registration_number}
                        </Link>
                    } />
                    <Row label="Make/Model" value={<span>{appt.vehicle?.make} {appt.vehicle?.model} {appt.vehicle?.year && `(${appt.vehicle.year})`}</span>} />
                </Card>

                <Card className="p-6">
                    <h2 className="mb-3 font-display text-lg font-bold tracking-tight">Service</h2>
                    <Row label="Type" value={<span className="capitalize">{(appt.service_type || "").replace(/_/g, " ")}</span>} />
                    <Row label="Source" value={<span className="capitalize">{appt.source || "—"}</span>} />
                    <Row label="Description" value={appt.description || "—"} />
                    <Row label="Duration" value={`${appt.estimated_duration_minutes || "—"} min`} />
                </Card>
            </div>

            {appt.converted_job_card_id && (
                <Card className="border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/30">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-sm">This appointment has a job card linked.</span>
                        <Link to={`/jobs/${appt.converted_job_card_id}`} className="ml-auto text-sm text-emerald-700 hover:underline dark:text-emerald-300 flex items-center gap-1">
                            Open job card <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Card>
            )}

            <Dialog open={showCancel} onOpenChange={setShowCancel}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Cancel this appointment?</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Reason</label>
                        <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Customer requested reschedule, vehicle unavailable…" data-testid="appt-cancel-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowCancel(false)}>Keep</Button>
                        <Button onClick={doCancel} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="appt-cancel-confirm">Cancel appointment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between border-b border-dashed border-zinc-200 py-2 last:border-b-0 dark:border-zinc-800">
            <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
            <span className="text-sm">{value}</span>
        </div>
    );
}
